/**
 * Data Explorer — view spec types.
 *
 * A DataView is a saved spec that resolves to a flat table. This is the single
 * source of truth shared by the explorer grid AND (later) the export feature, so
 * a view maps losslessly onto an export config.
 *
 * Ref: guide_documents/data_explorer_documents/01_architecture.md §2, §3.
 *
 * Free of runtime deps (no React, no Supabase) so the resolver + collapse logic
 * are unit-testable in isolation (apps/web/tests/unit/data-explorer.test.ts).
 */
import type { FieldType, CalculatedField } from '@/features/form-builder/api/form-builder-types'

// ─── Repeatable FORM axis (multiple submissions / instance_index per record) ──

/**
 * - `long`     — one table row per submission instance (server paginates by submission).
 * - `wide`     — one row per record; each instance's fields suffixed `@1`, `@2`… (capped).
 * - `aggregate`— one row per record; collapse instances via an aggregate fn.
 * - `pinned`   — one row per record; only the instance at `pinnedIndex`.
 *
 * `long` is the default for single-form auto views. `wide`/`aggregate`/`pinned`
 * require the grouped server layout. See §2 of the architecture doc.
 */
export type FormInstanceMode = 'long' | 'wide' | 'aggregate' | 'pinned'

export type AggregateFn = 'count' | 'first' | 'last' | 'min' | 'max' | 'avg'

export interface FormInstanceStrategy {
  mode: FormInstanceMode
  /** wide: max instance columns to emit (defaults to WIDE_DEFAULT_CAP). */
  cap?: number
  /** pinned: 1-based instance_index to keep. */
  pinnedIndex?: number
  /** aggregate: which fn (count needs no field; min/max/avg need a numeric field). */
  aggregate?: AggregateFn
}

// ─── Repeatable_array FIELD axis (array nested inside one submission's JSONB) ──

export type ArrayMode = 'join' | 'wide' | 'aggregate' | 'separate'

export interface ArrayStrategy {
  mode: ArrayMode
  /** join separator (default '، '). */
  separator?: string
  /** wide: max sub-instance columns. */
  cap?: number
  /** aggregate: count of entries, or sum of a numeric sub-field. */
  aggregate?: 'count' | 'sum'
  /** aggregate=sum / wide: which sub-field to surface. */
  subFieldKey?: string
}

// ─── Columns ──────────────────────────────────────────────────────────────────

export type ColumnSource = 'record_meta' | 'field'

export interface ViewColumn {
  /** Stable client id (used as React key + cell map key). */
  id: string
  source: ColumnSource
  /** record_meta key (see META_COLUMNS) OR a form field_key. */
  key: string
  /** field columns: the owning form (equals base.formId in single-form views). */
  formId?: string
  /** optional label override; otherwise the field/meta label is used. */
  label?: string
  width?: number
  hidden?: boolean
  /** repeatable_array fields: how to collapse the nested array into cells. */
  array?: ArrayStrategy
}

// ─── Filters & sort ─────────────────────────────────────────────────────────

export type FilterOp =
  | 'eq' | 'neq' | 'contains'
  | 'num_gt' | 'num_gte' | 'num_lt' | 'num_lte'
  | 'before' | 'after'
  | 'is_empty' | 'is_not_empty'
  | 'has_option'

export interface ViewFilter {
  id: string
  /** field_key the filter applies to (server-validated). */
  key: string
  op: FilterOp
  value?: string
  /** record-centric views: which form the field belongs to (single-form omits). */
  formId?: string
}

export interface ViewSort {
  /** a META_COLUMNS key, 'instance_index', or a field_key. */
  key: string
  dir: 'asc' | 'desc'
}

// ─── Base + full spec ─────────────────────────────────────────────────────────

export type ViewBase =
  | { kind: 'form'; formId: string }
  // record-centric multi-form base (architecture §5.2) — reserved for the next phase
  | { kind: 'record' }

export interface DataViewSpec {
  base: ViewBase
  /**
   * Repeatable-FORM collapse strategy for a SINGLE-FORM view (base.kind='form').
   * Ignored for record-centric views — those use per-form `formStrategies`.
   */
  instanceStrategy: FormInstanceStrategy
  /**
   * Record-centric (base.kind='record') only: per-form collapse strategy.
   * Keyed by form_id. Only `wide` / `aggregate` / `pinned` are valid here
   * (one row per record ⇒ no `long`). Single-instance forms ignore it.
   */
  formStrategies?: Record<string, FormInstanceStrategy>
  columns: ViewColumn[]
  filters: ViewFilter[]
  sort: ViewSort | null
  /** free-text search — OR-ILIKE across display_id + every shown field value. */
  search?: string
  includeDeleted?: boolean
  /** column header style — §15.6.1 export parity. */
  headerStrategy?: 'label' | 'field_key'
  /** prefix field columns with their form key — §15.6.2 export parity. */
  formPrefix?: boolean
}

/** Default per-form strategy in a record-centric view. */
export const DEFAULT_RECORD_FORM_STRATEGY: FormInstanceStrategy = { mode: 'aggregate', aggregate: 'last' }

// ─── Persisted row (data_views table projection) ──────────────────────────────

export type ViewVisibility = 'private' | 'project'

export interface DataViewRow {
  id: string
  project_id: string
  name: string
  description: string | null
  owner_id: string
  visibility: ViewVisibility
  spec: DataViewSpec
  is_default: boolean
  default_for_form_id: string | null
  version: number
  created_at: string
  updated_at: string
  owner_name?: string
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const WIDE_DEFAULT_CAP = 10

/** Record-level metadata columns the explorer can surface (no field lookup). */
export const META_COLUMNS = [
  { key: 'display_id', label: 'شناسه رکورد', type: 'text' as FieldType },
  { key: 'record_status', label: 'وضعیت', type: 'single_select' as FieldType },
  { key: 'record_created_at', label: 'تاریخ ایجاد', type: 'datetime' as FieldType },
  { key: 'record_updated_at', label: 'آخرین بروزرسانی', type: 'datetime' as FieldType },
  { key: 'instance_index', label: 'شماره نمونه', type: 'number' as FieldType },
  { key: 'submission_status', label: 'وضعیت ثبت', type: 'single_select' as FieldType },
  { key: 'soft_warning_count', label: 'هشدارها', type: 'number' as FieldType },
] as const

export type MetaColumnKey = (typeof META_COLUMNS)[number]['key']

/** Persian labels for record statuses (shared with the records list). */
export const RECORD_STATUS_LABELS: Record<string, string> = {
  draft: 'پیش‌نویس',
  in_progress: 'در جریان',
  submitted_for_review: 'ارسال برای بررسی',
  under_review: 'در حال بررسی',
  approved: 'تأیید شده',
  needs_revision: 'نیاز به اصلاح',
}

export const SUBMISSION_STATUS_LABELS: Record<string, string> = {
  draft: 'پیش‌نویس',
  submitted: 'ثبت‌شده',
}

/** Re-export so consumers get calc typing without reaching into form-builder. */
export type { CalculatedField }
